import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LuGift, LuSparkles, LuTarget } from "react-icons/lu";
import { heroLook, TIER_LABEL, tierOf, type EmoteKind } from "../../shared/avatar";
import type { Framing } from "../char3d/frame";
import { specFromEquipped } from "../char3d/types";
import { play } from "../sound";
import { useApp } from "../store";
import { Link } from "../router";
import { Avatar, AvatarBare, previewEquipped, usePortrait, type Equipped } from "./Avatar";
import { calm, coinShower, Confetti } from "./fx";
import { Coin } from "./ui";

/**
 * HEROES & SAVING GOAL (v17, v19). A hero is a complete named character (see src/shared/avatar.ts). Players save coins for ONE goal. Since v19 every hero is
 * shown in full colour (a child must be able to SEE who it is saving for – the black silhouettes of v17 hid it); progress is shown by the bar and a small lock.
 * Everything here is encouragement only: nothing is lost, nothing runs out, and the goal never blocks anything.
 */
const Stage = lazy(() => import("../char3d/stage"));

export interface HeroLike { id: string; name: string; data: Record<string, any> }
/** the shop item a player is saving for (what the server calls `goal`) */
export interface Goal extends HeroLike { category: string; price: number; rarity: string }

export const heroEquipped = (h: HeroLike): Equipped => heroLook(h) as Equipped;
export const fmt = (n: number) => n.toLocaleString("da-DK");
/** whole percent of the way (never shows 100 % before it is really reached) */
export const goalPct = (balance: number, price: number) => Math.max(0, Math.min(100, Math.floor((Math.max(0, balance) * 100) / Math.max(1, price))));
const tintOf = (d: Record<string, any> | undefined): [string, string] => ((d?.tint as [string, string] | undefined) ?? ["#4cc9f0", "#6366f1"]);
const FIGURE_LIKE = new Set(["skin", "outfit", "hat", "accessory", "back", "hand", "pet", "aura", "emote", "background", "frame"]);
const KIT: [string, string][] = [["outfit", "Tøj"], ["hat", "Hat"], ["accessory", "Ansigt"], ["back", "Ryg"], ["hand", "Udstyr"], ["pet", "Kæledyr"], ["aura", "Aura"], ["emote", "Sejrsanimation"]];
/** what a hero brings along, in words children know */
export const heroKit = (h: HeroLike): string[] => KIT.filter(([k]) => !!h.data?.parts?.[k]).map(([, l]) => l);

/** The hero as a picture: always in full colour. With 3D available the figure is a studio portrait, otherwise the flat drawing. */
export function HeroFigure({ hero, size = 160, enabled = true, framing = "full" }: { hero: HeroLike; size?: number; enabled?: boolean; framing?: Framing }) {
  const eq = useMemo(() => heroEquipped(hero), [hero.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const shot = usePortrait(eq, framing, size, { enabled });
  const [t1, t2] = tintOf(hero.data);
  return (
    <div className="hfig" style={{ width: size, height: size, ["--t1" as string]: t1, ["--t2" as string]: t2 }}>
      <span className="hf-glow" aria-hidden="true" />
      <div className="hf-layer hf-color" aria-hidden="true">
        {shot ? <img src={shot} alt="" draggable={false} /> : enabled ? <AvatarBare equipped={eq} size={Math.round(size * 0.8)} /> : null}
      </div>
    </div>
  );
}

/** a loose item (not a hero) as the goal's picture: the player's own figure wearing it, or a gift for things that are not worn */
function ItemArt({ goal, size }: { goal: Goal; size: number }) {
  const { me } = useApp();
  if (!FIGURE_LIKE.has(goal.category)) return <span className="gc-gift" style={{ width: size, height: size }}><LuGift aria-hidden="true" size={Math.round(size * 0.5)} /></span>;
  return <Avatar equipped={previewEquipped((me?.avatar ?? {}) as Equipped, goal)} size={size} label={`${goal.name} på din figur`} />;
}

/** The big saving-goal card (front page, shop). */
export function GoalCard({ goal, balance, onClear, className = "" }: { goal: Goal; balance: number; onClear?: () => void; className?: string }) {
  const pct = goalPct(balance, goal.price);
  const missing = Math.max(0, goal.price - balance);
  const ready = missing === 0;
  const isHero = goal.category === "hero";
  const [t1, t2] = tintOf(goal.data);
  return (
    <section className={`goalcard${ready ? " ready" : ""} ${className}`} aria-label={`Dit sparemål: ${goal.name}`} style={{ ["--t1" as string]: t1, ["--t2" as string]: t2 }}>
      <div className="gc-art">{isHero ? <HeroFigure hero={goal} size={140} /> : <ItemArt goal={goal} size={116} />}</div>
      <div className="gc-body">
        <span className="gc-kicker"><LuTarget aria-hidden="true" size={16} /> Dit sparemål</span>
        <h2 className="gc-name">{goal.name}</h2>
        {isHero && <p className="gc-epi">{String(goal.data.epithet ?? "")}</p>}
        <div className="gc-bar" role="progressbar" aria-label={`${pct} procent af vejen til ${goal.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <i style={{ width: `${Math.max(pct, 3)}%` }} />
          <b>{pct}%</b>
        </div>
        <p className="gc-line">
          {ready ? <strong>Du har råd! 🎉</strong> : <>Mangler <strong>{fmt(missing)}</strong> mønter</>} <small><Coin /> {fmt(Math.min(balance, goal.price))} af {fmt(goal.price)}</small>
        </p>
        <div className="gc-actions">
          {ready ? (
            <Link to={`/butik?valg=${goal.id}`} className="btn lime">Hent {goal.name}</Link>
          ) : (
            <>
              <Link to="/spil" className="btn lime">Spil og spar op</Link>
              <Link to={`/butik?valg=${goal.id}`} className="btn ghost">Se i butikken</Link>
            </>
          )}
          {onClear && <button type="button" className="btn ghost small" onClick={onClear}>Fjern mål</button>}
        </div>
      </div>
    </section>
  );
}

/** shown on the front page while the player has no goal yet */
export function GoalPrompt() {
  return (
    <Link to="/butik?fane=hero" className="goalprompt">
      <span className="gp-ico"><LuSparkles aria-hidden="true" size={28} /></span>
      <span className="gp-text"><b>Spar op til en helt!</b><small>Vælg din favorit</small></span>
      <span className="btn lime small" aria-hidden="true">Se heltene</span>
    </Link>
  );
}

/** a thin progress line for the end of a game: "Sparemål: Drage-Dorte 62 % (+4 %)" with the bar growing from the old to the new value */
export function GoalStrip({ goal, balance, earned }: { goal: Goal; balance: number; earned: number }) {
  const after = goalPct(balance, goal.price), before = goalPct(Math.max(0, balance - earned), goal.price);
  const [w, setW] = useState(calm() ? after : before);
  useEffect(() => {
    if (calm()) { setW(after); return; }
    const t = setTimeout(() => setW(after), 700);
    return () => clearTimeout(t);
  }, [after]);
  const ready = balance >= goal.price;
  const [t1, t2] = tintOf(goal.data);
  return (
    <div className={`goalstrip${ready ? " ready" : ""}`} style={{ ["--t1" as string]: t1, ["--t2" as string]: t2 }} role="status">
      <span className="gs-fig">{goal.category === "hero" ? <HeroFigure hero={goal} size={64} /> : <LuTarget aria-hidden="true" size={26} />}</span>
      <span className="gs-main">
        <span className="gs-top"><b>Sparemål: {goal.name}</b><span>{after}%{after > before ? <em> +{after - before}%</em> : null}</span></span>
        <span className="gs-bar" role="progressbar" aria-label={`${after} procent af vejen til ${goal.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={after}><i style={{ width: `${Math.max(w, 2)}%` }} /></span>
        <small>{ready ? "Du har råd til den nu!" : `Mangler ${fmt(goal.price - balance)} mønter`}</small>
      </span>
      <Link to={`/butik?valg=${goal.id}`} className={`btn small ${ready ? "lime" : "ghost"}`}>{ready ? "Hent den" : "Se"}</Link>
    </div>
  );
}

/**
 * The figure on a podium with coins, stars and cubes circling round it (the same 3D stage as the front page) – but only on a real graphics
 * card and without "less motion"; otherwise `fallback` (a flat picture) stays. The caller sizes the box.
 */
export function PodiumStage({ equipped, progress = 1, fallback, label, celebrate, className = "" }: { equipped: Equipped; progress?: number; fallback: ReactNode; label?: string; celebrate?: { kind: EmoteKind; n: number }; className?: string }) {
  const [mode, setMode] = useState<"checking" | "3d" | "flat">("checking");
  const spec = useMemo(() => specFromEquipped(equipped), [equipped]);
  useEffect(() => {
    let alive = true;
    if (calm()) { setMode("flat"); return; }
    import("../char3d/portrait").then((m) => { if (alive) setMode(m.hardwareGL() ? "3d" : "flat"); }).catch(() => alive && setMode("flat"));
    return () => { alive = false; };
  }, []);
  return (
    <div className={`pstage${mode === "3d" ? " live" : ""} ${className}`}>
      {mode === "3d" ? (
        <Suspense fallback={<div className="pst-fb">{fallback}</div>}>
          <Stage spec={spec} progress={progress} celebrate={celebrate} ariaLabel={label} onFail={() => setMode("flat")} />
        </Suspense>
      ) : (
        <div className="pst-fb">{fallback}</div>
      )}
    </div>
  );
}

/** a full-screen dialog: takes the focus, closes with Escape, keeps Tab inside and gives the focus back */
export function useDialog(ref: { current: HTMLElement | null }, onClose: () => void) {
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = Array.from(ref.current.querySelectorAll<HTMLElement>("button, a[href]")).filter((x) => !x.hasAttribute("disabled"));
        if (!f.length) return;
        const first = f[0]!, last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** the big "new hero unlocked!" moment after buying one */
export function UnlockCeremony({ hero, worn, busy, onWear, onClose }: { hero: Goal; worn: boolean; busy: boolean; onWear: () => void; onClose: () => void }) {
  const eq = useMemo(() => heroEquipped(hero), [hero]);
  const ref = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(0);
  const [t1, t2] = tintOf(hero.data);
  const kit = heroKit(hero);
  const emote = ((hero.data?.parts?.emote?.kind as EmoteKind | undefined) ?? "cheer");
  useEffect(() => {
    play("unlock");
    const a = setTimeout(() => { play("levelup"); setN(1); }, 450);
    const b = setTimeout(() => coinShower(ref.current?.querySelector<HTMLElement>(".ul-stage") ?? null, 34), 650);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);
  useDialog(ref, onClose);
  const tier = TIER_LABEL[tierOf(hero.rarity, hero.price)];
  return createPortal(
    <div className="unlock" role="dialog" aria-modal="true" aria-labelledby="ul-h" ref={ref} tabIndex={-1} style={{ ["--t1" as string]: t1, ["--t2" as string]: t2 }}>
      <div className="ul-rays" aria-hidden="true" />
      <Confetti n={70} ms={5000} />
      <div className="ul-card">
        <span className="ul-kicker"><LuSparkles aria-hidden="true" size={18} /> Ny helt låst op!</span>
        <div className="ul-stage">
          <PodiumStage equipped={eq} celebrate={{ kind: emote, n }} label={`${hero.name} på podiet – klik for at få helten til at reagere`} fallback={<HeroFigure hero={hero} size={240} framing="full" />} />
        </div>
        <span className={`tierchip tier-${tierOf(hero.rarity, hero.price)}`}>{tier}</span>
        <h2 id="ul-h" className="title-game ul-name">{hero.name}</h2>
        <p className="ul-epi">{String(hero.data.epithet ?? "")}</p>
        <p className="ul-lore">{String(hero.data.lore ?? "")}</p>
        {kit.length > 0 && <ul className="ul-kit" aria-label="Det følger med">{kit.map((k) => <li key={k}>{k}</li>)}</ul>}
        <div className="ul-actions">
          {worn ? <button type="button" className="btn lime" onClick={onClose}>Super – {hero.name} er på!</button> : <button type="button" className="btn lime" disabled={busy} onClick={onWear}>Tag {hero.name} på</button>}
          {!worn && <button type="button" className="btn ghost" onClick={onClose}>Senere</button>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
